import {TaskAttachment} from "../../../lib/src/api/dto/TaskAttachment"
import {TeamMembership} from "../../../lib/src/api/dto/TeamMembership"
import {WorkorderFeedback} from "../../../lib/src/api/dto/WorkorderFeedback"

export class EmployeeWorkDays {
  constructor(
    public id: string,
    public evenMonday: number,
    public evenTuesday: number,
    public evenWednesday: number,
    public evenThursday: number,
    public evenFriday: number,
    public evenSaturday: number,
    public evenSunday: number,
    public oddMonday: number,
    public oddTuesday: number,
    public oddWednesday: number,
    public oddThursday: number,
    public oddFriday: number,
    public oddSaturday: number,
    public oddSunday: number,
    public isTemporary: boolean,
    public startAt: Date,
    public endAt: Date|null,
  ) {
  }

  static fromJson(json: any): EmployeeWorkDays {
    return new EmployeeWorkDays(
      json['id'],
      json['even_mon'],
      json['even_tue'],
      json['even_wed'],
      json['even_thu'],
      json['even_fri'],
      json['even_sat'],
      json['even_sun'],
      json['odd_mon'],
      json['odd_tue'],
      json['odd_wed'],
      json['odd_thu'],
      json['odd_fri'],
      json['odd_sat'],
      json['odd_sun'],
      json['is_temporary'],
      new Date(json['start_at']),
      json['ended_at'] ? new Date(json['ended_at']) : null,
    )
  }
}

export class Supplier {
  constructor(
    public readonly id: string,
    public readonly name: string,
  ) {}

  static fromJson(json: any): Supplier {
    return new Supplier(
      String(json['id']),
      json['name'],
    );
  }
}

export class Holiday {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly date: Date,
    public readonly type: 'company_wide_vacation'|'national_holiday'|null,
  ) {
  }

  static fromJson(json: any): Holiday {
    return new Holiday(
      String(json['id']),
      json['name'],
      new Date(json['date']),
      json['type'],
    );
  }
}

export class GlobalHolidayYear {
  constructor(
    public readonly year: number,
    public readonly status: 'draft'|'published',
  ) {
  }

  static fromJson(json: any): GlobalHolidayYear {
    return new GlobalHolidayYear(
      Number(json['year']),
      json['status'],
    )
  }
}

export class TeamDayAvailability {
  constructor(public readonly teamId: string, public readonly employeeMemberships: TeamMembership[]) {
  }

  get employeeIds(): string[] {
    return this.employeeMemberships.map((membership) => membership.employeeId);
  }
}
export class TeamAvailability {
  constructor(
    public readonly weekStart: Date,
    public readonly weekEnd: Date,
    public readonly weekNumber: number,
    public readonly monday: TeamDayAvailability[],
    public readonly tuesday: TeamDayAvailability[],
    public readonly wednesday: TeamDayAvailability[],
    public readonly thursday: TeamDayAvailability[],
    public readonly friday: TeamDayAvailability[],
    public readonly saturday: TeamDayAvailability[],
    public readonly sunday: TeamDayAvailability[],
  ) {
  }

  static fromJson(json: any): TeamAvailability {
    return new TeamAvailability(
      new Date(json['startOfWeek']),
      new Date(json['endOfWeek']),
      json['week'],
      json['mon'].map((teamDayAvailability: any) => new TeamDayAvailability(teamDayAvailability['teamId'], teamDayAvailability['employeeMemberships'].map(TeamMembership.fromJson))),
      json['tue'].map((teamDayAvailability: any) => new TeamDayAvailability(teamDayAvailability['teamId'], teamDayAvailability['employeeMemberships'].map(TeamMembership.fromJson))),
      json['wed'].map((teamDayAvailability: any) => new TeamDayAvailability(teamDayAvailability['teamId'], teamDayAvailability['employeeMemberships'].map(TeamMembership.fromJson))),
      json['thu'].map((teamDayAvailability: any) => new TeamDayAvailability(teamDayAvailability['teamId'], teamDayAvailability['employeeMemberships'].map(TeamMembership.fromJson))),
      json['fri'].map((teamDayAvailability: any) => new TeamDayAvailability(teamDayAvailability['teamId'], teamDayAvailability['employeeMemberships'].map(TeamMembership.fromJson))),
      json['sat'] ? json['sat'].map((teamDayAvailability: any) => new TeamDayAvailability(teamDayAvailability['teamId'], teamDayAvailability['employeeMemberships'].map(TeamMembership.fromJson))) : [],
      json['sun'] ? json['sun'].map((teamDayAvailability: any) => new TeamDayAvailability(teamDayAvailability['teamId'], teamDayAvailability['employeeMemberships'].map(TeamMembership.fromJson))) : [],
    )
  }
}

export class PlanningAbsence {
  constructor(
    public readonly day: string,
    public readonly employeeId: string,
    public readonly reason: string,
  ) {
  }

  static fromJsonMap(json: {[day: string]: []|{[employeeId: string]: {employee_uuid: string, reason: string}}}): PlanningAbsence[] {
    return Object.entries(json).flatMap(([day, absences]) => {
      if (Array.isArray(absences)) {
        return [];
      }
      return Object.entries(absences).map(([employeeId, absence]) => {
        return new PlanningAbsence(day, employeeId, absence['reason']);
      });
    })
  }
}

export class Note {
  constructor(
    public readonly id: string,
    public readonly date: Date,
    public readonly lastModifiedByUserId: string,
    public readonly text: string,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
  ) {}

  static fromJson(json: any): Note {
    return new Note(
      json['id'],
      new Date(json['date']),
      json['last_modified_by_user_id'],
      json['text'],
      new Date(json['created_at']),
      new Date(json['updated_at']),
    );
  }
}

export class DailyRemark {
  constructor(
    public readonly id: string,
    public readonly team_id: string,
    public readonly author: string,
    public readonly date: Date,
    public readonly remark: string,
    public readonly createdAt: Date,
    public readonly updatedAt: Date,
  ) {
  }

  static fromJson(json: any): DailyRemark {
    return new DailyRemark(
      json['id'],
      json['team_id'],
      json['author'],
      new Date(json['date']),
      json['remark'],
      new Date(json['created_at']),
      new Date(json['updated_at']),
    )
  }
}
